'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import type { LinkProps } from 'next/link';
import { useTranslations } from 'next-intl';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { useFormatLink } from 'components/shared/CustomLink';
import { useModal } from 'components/shared/Modal';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { PerformanceLabTFModal } from 'components/performance-lab/WaitlistTypeformModal';
import { PERFORMANCE_LAB_CALLBACK_TYPEFORM_ID } from 'content/vo2MaxBannerConfig';
import { markPerformanceLabBookTourTypeformShown } from '../performanceLabBookTourTypeform';
import { PerformanceLabIndiaPurchaseModal } from 'components/performance-lab/PerformanceLabIndiaPurchaseModal';
import type {
  PerfLabDiscount,
  PerformanceLabPlan,
} from 'components/performance-lab/buy/india/types';
import {
  buildPerformanceLabCartLineItem,
  findCartLine,
  getPerformanceLabCartItemCount,
  resolvePerformanceLabAffiliateDiscount,
} from 'components/performance-lab/buy/india/performanceLabCartUtils';
import { getActivePlanOffer } from 'components/performance-lab/buy/india/planOffer';
import { LAB_PRICE_CUT_DISCOUNT_CODE } from 'components/performance-lab/buy/india/priceCut';
import { useAffiliateMetadata } from 'utils/affiliateHelpers';
import {
  getPerformanceLabCtaPath,
  isIndiaRegion,
  scrollToPerformanceLabSection,
  type PerformanceLabBuyCategory,
  type PerformanceLabBuyHrefOptions,
} from '../utils';

export const useIsIndiaRegion = () => {
  const { region, regionSlug } = useRegionLocale();
  return useMemo(() => isIndiaRegion(region, regionSlug), [region, regionSlug]);
};

export const usePerformanceLabCartItemCount = () => {
  const cart = useCartStore(useShallow((s) => ({ cart: s.cart })));
  return useMemo(() => getPerformanceLabCartItemCount(cart.cart), [cart.cart]);
};

const useOpenInternationalBookModal = () => {
  const router = useRouter();
  const formatLink = useFormatLink();
  const ModalClient = useModal();

  return useCallback(
    (buyHref: LinkProps['href']) => {
      const navigateToBuyPage = () => {
        ModalClient.hide();
        ModalClient.clear();
        router.push(formatLink(buyHref) as string);
      };

      ModalClient.set(
        React.createElement(PerformanceLabIndiaPurchaseModal, {
          onContinue: navigateToBuyPage,
        }),
      );
      ModalClient.setCloseButtonTheme('light');
      ModalClient.show();
    },
    [ModalClient, formatLink, router],
  );
};

export const useOpenBookTourModal = (defaultFlow = 'pl_book_tour') => {
  const ModalClient = useModal();

  return useCallback(
    (flow?: string) => {
      markPerformanceLabBookTourTypeformShown();
      ModalClient.set(
        React.createElement(PerformanceLabTFModal, {
          flow: typeof flow === 'string' ? flow : defaultFlow,
        }),
      );
      ModalClient.setCloseButtonTheme('light');
      ModalClient.show();
    },
    [ModalClient, defaultFlow],
  );
};

export type CallbackModalOptions = {
  flow?: string;
  source?: string;
  planId?: string;
};

const isCallbackOptions = (value: unknown): value is CallbackModalOptions => {
  if (!value || typeof value !== 'object') return false;
  if ('nativeEvent' in value || 'target' in value) return false;
  return 'flow' in value || 'source' in value || 'planId' in value;
};

export const useOpenCallbackModal = (defaultFlow = 'pl_callback') => {
  const ModalClient = useModal();

  return useCallback(
    (options?: CallbackModalOptions | React.SyntheticEvent) => {
      const opts = isCallbackOptions(options) ? options : undefined;
      const flow = opts?.flow || opts?.planId || opts?.source || defaultFlow;
      ModalClient.set(
        React.createElement(PerformanceLabTFModal, {
          typeformId: PERFORMANCE_LAB_CALLBACK_TYPEFORM_ID,
          flow,
        }),
      );
      ModalClient.setCloseButtonTheme('light');
      ModalClient.show();
      ModalClient.setPortal(true);
    },
    [ModalClient, defaultFlow],
  );
};

type HandleBookNowOptions = {
  category?: PerformanceLabBuyCategory;
};

export const useHandleBookNow = (options?: HandleBookNowOptions) => {
  const router = useRouter();
  const isIndia = useIsIndiaRegion();
  const { region, regionSlug } = useRegionLocale();
  const formatLink = useFormatLink();
  const openInternationalBookModal = useOpenInternationalBookModal();
  const buyHref = getPerformanceLabCtaPath(region, regionSlug, {
    category: options?.category,
  });

  return useCallback(() => {
    if (isIndia) {
      router.push(formatLink(buyHref) as string);
      return;
    }

    openInternationalBookModal(buyHref);
  }, [buyHref, formatLink, isIndia, openInternationalBookModal, router]);
};

export const useHandlePerformanceLabBuyNavigate = () => {
  const router = useRouter();
  const isIndia = useIsIndiaRegion();
  const { region, regionSlug } = useRegionLocale();
  const formatLink = useFormatLink();
  const openInternationalBookModal = useOpenInternationalBookModal();

  return useCallback(
    (options?: PerformanceLabBuyHrefOptions) => {
      const buyHref = getPerformanceLabCtaPath(region, regionSlug, options);

      if (isIndia) {
        router.push(formatLink(buyHref) as string);
        return;
      }

      openInternationalBookModal(buyHref);
    },
    [
      formatLink,
      isIndia,
      openInternationalBookModal,
      region,
      regionSlug,
      router,
    ],
  );
};

type HandlePlanSelectOptions = {
  category?: PerformanceLabBuyCategory;
};

/**
 * Affiliate links reach us as either `?affiliateCode=` or `?affiliate=` — both
 * are in `FORWARDED_PARAM_KEYS`, and Ring Pro / Diesel already accept either.
 * Performance Labs has to as well, or an `?affiliate=` link silently prices at
 * full while the same link discounts a ring.
 */
const useAffiliateCode = (): string => {
  const searchParams = useSearchParams();
  return (
    searchParams?.get('affiliateCode') ?? searchParams?.get('affiliate') ?? ''
  );
};

/**
 * The single source of Performance Labs affiliate pricing.
 *
 * Reads the affiliate code off the URL, fetches that affiliate's config, and
 * returns a resolver that answers per plan — Performance Labs discounts can be
 * scoped to specific products, so "what is the discount" only has an answer
 * once you name a plan.
 *
 * Deliberately provider-free: it depends only on the URL, so a plan card can
 * price itself outside `PerformanceLabsIndiaProvider`.
 */
export const usePerformanceLabAffiliateDiscount = () => {
  const affiliateCode = useAffiliateCode();
  const { affiliateDetails } = useAffiliateMetadata(affiliateCode);

  const getPlanDiscount = useCallback(
    (shopifyVariantId?: string): PerfLabDiscount =>
      resolvePerformanceLabAffiliateDiscount(
        affiliateCode,
        affiliateDetails,
        shopifyVariantId,
      ),
    [affiliateCode, affiliateDetails],
  );

  const getDiscountedPrice = useCallback(
    (price: number, shopifyVariantId?: string) => {
      const { percentage } = getPlanDiscount(shopifyVariantId);
      return Math.round(price * (1 - percentage / 100));
    },
    [getPlanDiscount],
  );

  return { getPlanDiscount, getDiscountedPrice };
};

export const useHandlePlanSelect = (
  plan: PerformanceLabPlan,
  options?: HandlePlanSelectOptions,
) => {
  const router = useRouter();
  const isIndia = useIsIndiaRegion();
  const { region, regionSlug } = useRegionLocale();
  const formatLink = useFormatLink();
  const openInternationalBookModal = useOpenInternationalBookModal();
  const cart = useCartStore(
    useShallow((s) => ({
      cart: s.cart,
      addToCartSilently: s.addToCartSilently,
      showCart: s.showCart,
    })),
  );
  const [isAdding, setIsAdding] = useState(false);

  const { getPlanDiscount } = usePerformanceLabAffiliateDiscount();

  // Scoped to THIS plan — a catalogue-wide code and a code that only covers
  // diagnostics must not both attach to an IV drip.
  const affiliateDiscount = useMemo(
    () => getPlanDiscount(plan.shopifyVariantId),
    [getPlanDiscount, plan.shopifyVariantId],
  );

  const navigateToBuyPage = useCallback(() => {
    const buyHref = getPerformanceLabCtaPath(region, regionSlug, {
      category: options?.category,
      planId: plan.id,
    });
    router.push(formatLink(buyHref) as string);
  }, [formatLink, options?.category, plan.id, region, regionSlug, router]);

  const handlePlanSelect = useCallback(async () => {
    if (!isIndia) {
      const buyHref = getPerformanceLabCtaPath(region, regionSlug, {
        category: options?.category,
        planId: plan.id,
      });
      openInternationalBookModal(buyHref);
      return;
    }

    if (!plan.shopifyVariantId) {
      navigateToBuyPage();
      return;
    }

    const alreadyInCart = Boolean(
      findCartLine(cart.cart, plan.shopifyVariantId),
    );

    if (alreadyInCart) {
      return;
    }

    setIsAdding(true);
    try {
      // Same precedence as buy-page addToCart: EXINPL15 yields to affiliate;
      // other standing offers (no code / Shopify-cut) still win over affiliate.
      const activeOffer = getActivePlanOffer(plan);
      const yieldExinpl15 =
        activeOffer?.discountCode === LAB_PRICE_CUT_DISCOUNT_CODE &&
        affiliateDiscount.percentage > 0 &&
        Boolean(affiliateDiscount.discountCode);
      const discountCode = yieldExinpl15
        ? affiliateDiscount.discountCode || undefined
        : activeOffer
          ? activeOffer.discountCode || undefined
          : affiliateDiscount.discountCode || undefined;
      await cart.addToCartSilently([
        buildPerformanceLabCartLineItem(plan.shopifyVariantId, discountCode),
      ]);
      cart.showCart();
    } finally {
      setIsAdding(false);
    }
  }, [
    affiliateDiscount.discountCode,
    affiliateDiscount.percentage,
    cart,
    isIndia,
    navigateToBuyPage,
    openInternationalBookModal,
    options?.category,
    plan,
    region,
    regionSlug,
  ]);

  return { handlePlanSelect, isAdding };
};

export const useHandleRecoveryBookNow = () =>
  useHandleBookNow({ category: 'recovery' });

export const usePerformanceLabCTALabel = (
  labelKey: string,
  labelDefault: string,
) => {
  const t = useTranslations('performance-lab');
  return t.has(labelKey) ? t(labelKey) : labelDefault;
};

export const useScrollToPerformanceLabSection = (elementId: string) =>
  useCallback(
    () => scrollToPerformanceLabSection(elementId),
    [elementId],
  );
